import { Trans, useLingui } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@/components/ui-kit/button";
import { RichTextContent } from "@/components/ui-kit/rich-text-content";

import { UpdateSeerlinqStudyText } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { useCountries } from "../../../catalogs/countries";
import { Spinner } from "../../../components/ui-kit/spinner";
import { assert } from "../../../utils/utils";
import { AddStudyText } from "./add-study-text";
import { StudyTextsForm, StudyTextsFormFields } from "./study-texts-form";

export const ConsentsFormRoute = observer(function ConsentsFormRoute(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const params = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const admin = props.app.requireAdminShit();
  const model = admin.studyTexts;
  assert(model, "Study texts model not initialized");
  const countries = useCountries();

  const isAddMode = !params.uuid;

  const selectedItem = isAddMode
    ? null
    : (model.data.find((i) => i.uuid === params.uuid) ?? null);

  const [isEditMode, setIsEditMode] = useState(false);
  const [editFields] = useState<StudyTextsFormFields>(() =>
    observable({
      monitoring_type: null,
      study_text_language: null,
      study_text: "",
      country: null,
      region: null,
      clinic_uuid: null,
      physician_uuid: null,
      clinic_name: "",
      physician_name: "",
    }),
  );
  const [addFields] = useState(() => new AddStudyText(model));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditOrAddMode = isAddMode || isEditMode;

  const enterEditMode = () => {
    if (!selectedItem) return;
    editFields.monitoring_type = selectedItem.monitoring_type;
    editFields.study_text_language = selectedItem.study_text_language;
    editFields.study_text = selectedItem.study_text ?? "";
    editFields.country = selectedItem.country ?? null;
    editFields.region = selectedItem.region ?? null;
    editFields.clinic_uuid = selectedItem.clinic_uuid ?? null;
    editFields.physician_uuid = selectedItem.physician_uuid ?? null;
    editFields.clinic_name = selectedItem.clinicName;
    editFields.physician_name = selectedItem.physicianName;
    setIsEditMode(true);
  };

  const handleSave = async () => {
    if (isAddMode) {
      try {
        setIsSaving(true);
        const uuid = await addFields.post();
        void navigate(`/admin/settings/consents/${uuid}`);
        setError(null);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t`Failed to create study text`,
        );
      } finally {
        setIsSaving(false);
      }
    } else {
      if (!selectedItem) return;
      try {
        setIsSaving(true);
        const patch: UpdateSeerlinqStudyText = {};
        if (editFields.monitoring_type !== selectedItem.monitoring_type)
          patch.monitoring_type = editFields.monitoring_type;
        if (editFields.study_text_language !== selectedItem.study_text_language)
          patch.study_text_language = editFields.study_text_language;
        if (editFields.study_text !== (selectedItem.study_text ?? ""))
          patch.study_text = editFields.study_text;
        if (editFields.country !== (selectedItem.country ?? null))
          patch.country = editFields.country;
        if (editFields.region !== (selectedItem.region ?? null))
          patch.region = editFields.region;
        if (editFields.clinic_uuid !== (selectedItem.clinic_uuid ?? null))
          patch.clinic_uuid = editFields.clinic_uuid;
        if (editFields.physician_uuid !== (selectedItem.physician_uuid ?? null))
          patch.physician_uuid = editFields.physician_uuid;

        await model.updateStudyText(selectedItem.uuid, patch);
        setError(null);
        setIsEditMode(false);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t`Failed to save changes`,
        );
      } finally {
        setIsSaving(false);
      }
    }
  };

  const activeFields: StudyTextsFormFields = isAddMode ? addFields : editFields;
  const canSave =
    activeFields.monitoring_type != null &&
    activeFields.study_text_language != null &&
    activeFields.study_text.trim().length > 0;

  if (!isAddMode && !selectedItem) {
    return (
      <div>
        <p className="text-danger-foreground">
          <Trans>Study text not found.</Trans>
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-6 mt-6 bg-card shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">
          {isAddMode
            ? t`Add New Study Text`
            : `${selectedItem?.monitoringTypeText} - ${selectedItem?.languageText}`}
        </h1>
        <div className="flex gap-2">
          {!isAddMode && !isEditMode && (
            <Button onClick={enterEditMode}>
              <Trans>Edit</Trans>
            </Button>
          )}
          {isEditOrAddMode && (
            <>
              {isEditMode && (
                <Button
                  onClick={() => {
                    setIsEditMode(false);
                    setError(null);
                  }}
                >
                  <Trans>Cancel</Trans>
                </Button>
              )}
              <Button
                onClick={handleSave}
                disabled={isSaving || !canSave}
                className="gap-2"
              >
                {isSaving ? <Spinner /> : null}
                {isSaving
                  ? isAddMode
                    ? t`Creating...`
                    : t`Saving...`
                  : isAddMode
                    ? t`Create`
                    : t`Save Changes`}
              </Button>
            </>
          )}
        </div>
      </div>

      {error && (
        <p className="text-danger-foreground mb-4">
          <Trans>Error: {error}</Trans>
        </p>
      )}

      {isEditOrAddMode ? (
        <StudyTextsForm fields={activeFields} admin={admin} />
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            <div>
              <h3 className="text-sm font-semibold mb-1 text-muted-foreground">
                <Trans>Country</Trans>
              </h3>
              <p className="text-foreground">{selectedItem?.country || "—"}</p>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-1 text-muted-foreground">
                <Trans>Region</Trans>
              </h3>
              <p className="text-foreground">
                {selectedItem && selectedItem?.country && selectedItem?.region
                  ? countries.getRegionSchema(
                      selectedItem?.country,
                      selectedItem?.region,
                    )?.region_name
                  : "—"}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-1 text-muted-foreground">
                <Trans>Clinic</Trans>
              </h3>
              <p className="text-foreground">
                {selectedItem?.clinicName || "—"}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-1 text-muted-foreground">
                <Trans>Physician</Trans>
              </h3>
              <p className="text-foreground">
                {selectedItem?.physicianName || "—"}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-1 text-muted-foreground">
                <Trans>Version</Trans>
              </h3>
              <p className="text-foreground">
                {selectedItem?.study_text_version ?? "—"}
              </p>
            </div>
          </div>
          <div className="border-t pt-2">
            <div className="prose prose-sm max-w-full min-h-40">
              {selectedItem?.study_text ? (
                <RichTextContent value={selectedItem.study_text} />
              ) : (
                <p className="text-muted-foreground">
                  <Trans>No content</Trans>
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
});
